/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useRef, useState } from 'react';
import Footer from '../Footer/Footer';
import SideMenu from '../dashboard/sideMenu/SideMenu';
import { useSelector, useDispatch } from 'react-redux';

import {
  RootState,
  bankAccountDetailRequest,
  fetchRewardAccountRequest,
  eppRequest,
  environment,
  passkeyStatusRequest,
} from '@rb-serve-app/core';

import { AnalyticsProvider } from '../Analytics';
import Header from '../header/Header';
import Spinner from '../../components/Spinner';
import FeatureFlagMessage from '../../components/FeatureFlagMessage';
import { useLocation, useSearchParams } from 'react-router-dom';
import Error500 from '../../components/Errors/Error500';

import {
  cleanupBrazeOnLogout,
  identifyBrazeUser,
  initializeBrazeWeb,
  setBrazeAuthenticationSignature,
} from '../../config/braze';

interface LayoutProps {
  children: React.ReactNode;
}

interface Feature {
  feature: string;
  accountAccessRestricted: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const locale = searchParams.get('locale');

  const [isApiError, setIsApiError] = useState(false);
  const [showFeatureFlagMessage, setShowFeatureFlagMessage] = useState(false);
  const [isUnlinkModal, setShowUnlinkModal] = useState(false);

  const { error, isAuthenticated, accountDetails, accountId, customerId } =
    useSelector((state: RootState) => state?.user);

  const { bankAccountDetail } = useSelector(
    (state: RootState) => state.profile
  );

  const { rewardAccountDetails } = useSelector(
    (state: RootState) => state.banLinkage
  );

  const { eppDetails } = useSelector((state: RootState) => state.epp);
  const { isRegistered: isPasskeyRegistered } = useSelector(
    (state: RootState) => state.passkeyStatus ?? { isRegistered: null }
  );

  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const hasFetched = useRef(false);
  const wasAuthenticatedRef = useRef(false);

  const brazeInitializedUserRef = useRef<string | null>(null);

  const brazeInitializationInProgressRef = useRef(false);

  const isSignInPage = location.pathname.startsWith('/sign-in');

  useEffect(() => {
    if (error?.statusCode === 500) {
      setIsApiError(true);
    }
  }, [error]);

  const handelApiErrorpopup = () => {
    setIsApiError(false);
  };

  useEffect(() => {
    if (!accountId || !customerId || accountDetails == null) {
      return;
    }

    if (hasFetched.current) {
      return;
    }

    hasFetched.current = true;

    dispatch(
      bankAccountDetailRequest({
        accId: accountId,
      })
    );

    dispatch(
      fetchRewardAccountRequest({
        user_id: accountId.replace(/^0+/, ''),
      })
    );

    if (!eppDetails?.installData?.installmentPlans) {
      dispatch(
        eppRequest({
          accountId,
          customerId,
        })
      );
    }
    dispatch(passkeyStatusRequest({ accountId, customerId }));
  }, [accountId, customerId, dispatch, eppDetails, accountDetails]);

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return;
    }

    const brazeUserId = String(customerId);

    if (
      brazeInitializedUserRef.current === brazeUserId ||
      brazeInitializationInProgressRef.current
    ) {
      return;
    }

    brazeInitializationInProgressRef.current = true;

    let cancelled = false;

    const initializeBrazeForUser = async () => {
      try {
        const initialized = initializeBrazeWeb({
          env: environment.env,
          brazeWeb: environment.brazeWeb,
        });

        if (!initialized) {
          console.error('[Braze] SDK initialization failed.');

          return;
        }

        if (cancelled) {
          return;
        }

        const signature = await setBrazeAuthenticationSignature();

        if (cancelled) {
          return;
        }

        if (!signature) {
          console.error('[Braze] SDK authentication signature is missing.');

          return;
        }

        console.log('[Braze] Identifying customer:', brazeUserId);
        console.log('[Braze] Using signature:', signature);
        const identified = identifyBrazeUser(
          brazeUserId,
          {
            accountId: accountId ? String(accountId) : undefined,

            customerId: brazeUserId,

            accountType: accountDetails?.productName,

            locale: locale === 'fr' ? 'fr' : 'en',
          },
          signature
        );

        if (!identified) {
          console.error('[Braze] Failed to identify customer.');

          return;
        }

        if (cancelled) {
          return;
        }

        brazeInitializedUserRef.current = brazeUserId;

        console.log('[Braze] Customer initialized successfully:', brazeUserId);
      } catch (error) {
        console.error('[Braze] Customer initialization failed:', error);
      } finally {
        brazeInitializationInProgressRef.current = false;
      }
    };

    void initializeBrazeForUser();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    customerId,
    accountId,
    accountDetails?.productName,
    locale,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticatedRef.current = true;
      return;
    }

    if (wasAuthenticatedRef.current) {
      cleanupBrazeOnLogout();

      brazeInitializedUserRef.current = null;
      brazeInitializationInProgressRef.current = false;

      wasAuthenticatedRef.current = false;
    }
  }, [isAuthenticated]);

  const dynaScriptUrl = environment.dynaBeaconUrl;

  useEffect(() => {
    if (!accountId) {
      return;
    }

    if (sessionStorage.getItem('dynatraceInit') === 'true') {
      console.log('Dynatrace return because already initialized');

      return;
    }

    const sendDynatraceAccountTag = (id: any) => {
      try {
        const sId = id != null ? String(id) : null;

        console.log('Sending Dynatrace account tag for', sId);

        const dtrum = (window as any).dtrum;

        if (dtrum && typeof dtrum.identifyUser === 'function') {
          dtrum.identifyUser(sId);

          sessionStorage.setItem('dynatraceInit', 'true');
        }

        if (dtrum && typeof dtrum.enterAction === 'function') {
          try {
            dtrum.enterAction('account-tag', {
              accountId: sId,
            });
          } catch (err) {
            console.warn('dtrum.enterAction failed', err);
          }
        }
      } catch (e) {
        console.warn('Failed to tag Dynatrace with accountId', e);
      }
    };

    if (!document.querySelector('script[src*="ruxitagent_D_"]')) {
      const script = document.createElement('script');

      script.src = `${dynaScriptUrl}`;
      script.async = true;

      document.head.appendChild(script);

      script.onload = () => {
        console.log('Dynatrace RUM loaded');

        sendDynatraceAccountTag(accountId);
      };

      script.onerror = () => console.error('Dynatrace RUM failed to load');
    }

    const interval = setInterval(() => {
      if (
        (window as any).dtrum &&
        typeof (window as any).dtrum.identifyUser === 'function'
      ) {
        console.log(
          'Dynatrace ready ✅',
          (window as any).dtrum,
          typeof (window as any).dtrum.identifyUser
        );

        sendDynatraceAccountTag(accountId);

        clearInterval(interval);
      } else {
        console.log('Waiting for Dynatrace...');
      }
    }, 500);

    return () => clearInterval(interval);
  }, [accountId, dynaScriptUrl]);

  const { entries, assets } = contentfulData || {};

  const featureFlaggingData = entries?.rb_feature_flagging?.featureFlagging;

  const derogatoryData = entries?.rb_more;

  const showMainLayout = !isSignInPage && isAuthenticated;

  const isLinkAcc =
    bankAccountDetail?.bankAccountResponse?.linkageStatus === '1' &&
    rewardAccountDetails?.redeemerProfile?.accountInfo?.accountStatus === '1';

  const isEppVisible = eppDetails?.installData?.installmentPlans?.length >= 1;

  const isAccountRestricted = (): boolean => {
    if (
      accountDetails &&
      accountDetails.features &&
      accountDetails.features.length > 0
    ) {
      const communicationsFeature = accountDetails.features.find(
        (feature: Feature) => feature.feature === 'COMMUNICATIONS_ACCESS'
      );

      if (
        communicationsFeature &&
        communicationsFeature.accountAccessRestricted
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <AnalyticsProvider>
      {entries ? (
        <div className="flex flex-col min-h-screen">
          {isApiError && (
            <Error500 isOpen={isApiError} closeModal={handelApiErrorpopup} />
          )}

          {showMainLayout && (
            <Header
              contentfulData={entries?.rb_header}
              contentSideNav={entries}
              featureFlaggingData={featureFlaggingData}
              setShowFeatureFlagMessage={setShowFeatureFlagMessage}
              isLinkAcc={isLinkAcc}
              isAccountRestricted={isAccountRestricted}
              derogatoryData={derogatoryData}
              isEppVisible={isEppVisible}
              isPasskeyRegistered={isPasskeyRegistered === true}
            />
          )}

          <div className="flex flex-1 relative">
            {showMainLayout && (
              <SideMenu
                contentfulData={entries?.rb_navigation}
                featureFlaggingData={featureFlaggingData}
                setShowFeatureFlagMessage={setShowFeatureFlagMessage}
                isLinkAcc={isLinkAcc}
                isAccountRestricted={isAccountRestricted}
                assets={assets}
                derogatoryData={derogatoryData}
                isEppVisible={isEppVisible}
                isPasskeyRegistered={isPasskeyRegistered === true}
                isUnlinkModal={isUnlinkModal}
                setShowUnlinkModal={setShowUnlinkModal}
              />
            )}

            {showFeatureFlagMessage && (
              <FeatureFlagMessage
                className={isSignInPage ? 'left-0' : 'md:left-20 left-0'}
              />
            )}

            <div
              className={`flex-1 flex flex-col bg-[#FAFAFA] border-0 border-l w-full md:w-[calc(100vw-100px)] ${
                entries?.rb_error?.isWebSiteDown ? 'mb-0' : 'mb-5'
              }`}
            >
              {children}

              <Footer
                siteDown={entries?.rb_error?.isWebSiteDown}
                contentfulData={entries}
                assetsData={assets}
                featureFlaggingData={featureFlaggingData}
                setShowFeatureFlagMessage={setShowFeatureFlagMessage}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-[80vh] justify-center items-center">
          <Spinner />
        </div>
      )}
    </AnalyticsProvider>
  );
};

export default Layout;
